/* eslint-disable complexity */
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Typography } from 'carbon-react/lib/components/typography/typography.component';

import { FormattedMessage } from '@futrli/localisation';
import { ExtendedReportTemplateList } from '@futrli/views/processors/reports/reportTemplatesList';

import { ActionDialogWizard, useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard';
import { useApiView } from '@futrli/views/hooks/useApiView';
import { ModifyReportTemplateView } from '@futrli/views/views/reports/ModifyReportTemplateView/ModifyReportTemplateView';

import { useRouteIds } from '../../../hooks/useRouteIds';

interface PublishTemplateDialogContainerProps {
  isOpen: boolean;
  onClose: () => void;
  template: ExtendedReportTemplateList;
  isFutrliTemplate?: boolean;
}

export const PublishTemplateDialogContainer = ({
  isOpen,
  onClose,
  template,
  isFutrliTemplate = false
}: PublishTemplateDialogContainerProps) => {
  const intl = useIntl();
  const { workspaceId, organisationId, isConsolidation } = useRouteIds();
  const isPublishing = template.actions.publish;

  const view = useApiView(
    useMemo(() => new ModifyReportTemplateView(workspaceId, organisationId, isConsolidation), [
      workspaceId,
      organisationId,
      isConsolidation
    ])
  );

  const { screen, handleAction: handleConfirm, handleClose } = useDialogWizardState({
    isLoading: view.state.isPublishingTemplate,
    error: view.state.errorPublishingTemplate,
    onAction: () =>
      view.publishTemplate({
        templateId: template.id,
        status: isPublishing ? 'published' : 'draft',
        ...(isFutrliTemplate && { publishFormulas: true })
      }),
    onClose
  });

  const title = isPublishing
    ? intl.formatMessage({ id: 'reporting.publish_template', defaultMessage: 'Publish template' })
    : intl.formatMessage({ id: 'reporting.unpublish_template', defaultMessage: 'Unpublish template' });

  const confirmDescription = isPublishing ? undefined : (
    <Typography fontSize="14px" fontWeight="500">
      {intl.formatMessage({
        id: 'reporting.unpublish_template_description',
        defaultMessage: 'This is a published template'
      })}
    </Typography>
  );

  const thingsToKnowTitle = isPublishing ? (
    <>
      <FormattedMessage
        id="reporting.things_to_know_before_publishing"
        defaultMessage="Things to know before publishing"
      />
      {':'}
    </>
  ) : undefined;

  const futrliThingsToKnow = isPublishing
    ? [
        <FormattedMessage
          key="futrli_live"
          id="reporting.publish_futrli_template_things_to_know_live"
          defaultMessage="Publishing makes this Futrli template live and available to all Futrli customers."
        />,
        <FormattedMessage
          key="futrli_formulas"
          id="reporting.publish_futrli_template_things_to_know_formulas"
          defaultMessage="Any draft template formulas used by this template will be published with it."
        />
      ]
    : [
        <FormattedMessage
          key="futrli_remove"
          id="reporting.unpublish_futrli_template_things_to_know_remove"
          defaultMessage="Unpublishing this template will remove it from all customer workspaces."
        />,
        <FormattedMessage
          key="reports"
          id="reporting.unpublish_template_things_to_know_reports"
          defaultMessage="Any report previously created from this template will be unaffected."
        />
      ];

  const thingsToKnow = isFutrliTemplate
    ? futrliThingsToKnow
    : isPublishing
    ? [
        <FormattedMessage
          key="sensitive"
          id="reporting.publish_template_things_to_know_sensitive"
          defaultMessage="Please ensure that there is no sensitive information within Text Widgets prior to publishing this to your workspace."
        />,
        <FormattedMessage
          key="guest"
          id="reporting.publish_template_things_to_know_guest"
          defaultMessage="Guest users will not have access to any workspace templates."
        />,
        <FormattedMessage
          key="organisations"
          id="reporting.publish_template_things_to_know_organisations"
          defaultMessage="Reports may be created from this template in any of the organisations non-guest users have access to."
        />,
        <FormattedMessage
          key="unpublish"
          id="reporting.publish_template_things_to_know_unpublish"
          defaultMessage="Workspace templates may be unpublished and set to a draft state at any time."
        />
      ]
    : [
        <FormattedMessage
          key="remove"
          id="reporting.unpublish_template_things_to_know_remove"
          defaultMessage="Unpublishing this template will remove it from all non-guest workspaces."
        />,
        <FormattedMessage
          key="reports"
          id="reporting.unpublish_template_things_to_know_reports"
          defaultMessage="Any report previously created from this template will be unaffected."
        />
      ];

  const loadingMessage = isPublishing
    ? intl.formatMessage({ id: 'reporting.publishing_template', defaultMessage: 'Publishing template...' })
    : intl.formatMessage({ id: 'reporting.unpublishing_template', defaultMessage: 'Unpublishing template...' });

  const successMessage = isPublishing
    ? intl.formatMessage({ id: 'reporting.template_published', defaultMessage: 'Template published' })
    : intl.formatMessage({ id: 'reporting.template_unpublished', defaultMessage: 'Template unpublished' });

  const confirmButtonText = isPublishing
    ? intl.formatMessage({ id: 'reporting.publish_template', defaultMessage: 'Publish template' })
    : intl.formatMessage({ id: 'reporting.unpublish_template', defaultMessage: 'Unpublish template' });

  const errorMessage = view.state.errorPublishingTemplate?.message;

  return (
    <ActionDialogWizard
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
      screen={screen}
      title={title}
      subtitle={template.name}
      icon={{ type: 'info', color: '#0060A7' }}
      confirmDescription={confirmDescription}
      thingsToKnow={thingsToKnow}
      thingsToKnowTitle={thingsToKnowTitle}
      confirmButtonText={confirmButtonText}
      loadingMessage={loadingMessage}
      successMessage={successMessage}
      errorMessage={errorMessage}
      dataTestId="publish-template-dialog"
    />
  );
};
